import { CropImage } from '@/business-components/CropImage';
import { openUrl } from '@/common/bridge/openUrl';
import { $k18n } from '@/common/i18n';
import { KImage, KText, KView } from '@/component/RtlLayout';
import usePriceAfterCoupon from '@/hooks/abTest/usePriceAfterCoupon';
import { useQuery, useQuerys } from '@/hooks/useQuery';
import { logClickGoToDetail } from '@/page/detail/views/logger2.0';
import { useInstallmentFirstThresholdValue } from '@/page/detail/views/useABHandler';
import { useLatest } from 'ahooks';
import React, { useMemo } from 'react';
import { TouchableWithoutFeedback, Text, View } from 'react-native';
import { showFlashActivity, useExtParamsObject } from '../../../utils';
import SrcStyles from '../index.styl';
import { convertStyles } from '@kibt/krn-utils';
import EnlargePng from '@/assets/icon_detail/enlarge.png';
import { newFormatNumber } from '../../../18nText';
import { currentCountry } from '@/common/const';
import IconDown from '@/assets/images_detail/icon-down.png';
import { Stepper } from '@/component/Stepper/Stepper';
import { Toast } from '@kibt/meta-ui-krn';
import { ItemSpecValue, PageRowData, Sku } from '@/page/detail/views/api';
import queryString from 'query-string';
import { getEnv, getKrnDomain } from '@/common/api';
const Styles = convertStyles(SrcStyles);

interface SpecHeaderProps {
    pageRawData: PageRowData;
    specImageIndex: number;
    currentSku: Sku;
    showDetailUrl?: string | boolean;
    isNotAllImg: boolean;
    detailABValueMap: { [key: string]: any };
    showAfterCouponPrice: boolean;
    skuInfo: Sku[];
    selectedItems: ItemSpecValue[];
    clickImgPre: Function;
    type: string;
    showUpStepper: boolean;
    immediateAvailableQuantity: number;
    purchaseQuantity: number;
    setPurchaseQuantity: Function;
    scene?: number | string;
    extParamsCustom?: { [key: string]: string };
}

export function SpecHeader(props: SpecHeaderProps) {
    const {
        pageRawData = {} as PageRowData,
        specImageIndex,
        currentSku,
        showDetailUrl,
        isNotAllImg,
        detailABValueMap,
        showAfterCouponPrice,
        skuInfo,
        selectedItems,
        clickImgPre,
        type,
        showUpStepper,
        immediateAvailableQuantity,
        purchaseQuantity,
        setPurchaseQuantity,
        scene,
        extParamsCustom,
    } = props;

    const isToBDetail = useQuery('pageType') === 'seller';
    const { thresholdValue } = useInstallmentFirstThresholdValue();
    const minNum = (pageRawData?.minPurchase as number) ?? 1;
    const { sellerUserId, merchantId } = useQuerys();
    const extParamsObj = useExtParamsObject();
    function goDetail(url) {
        let _url;
        if (typeof url === 'string' && url !== '') {
            _url = url;
        } else {
            const krnUrl = queryString.stringify(
                {
                    bundleId: 'Detail',
                    componentName: 'Detail',
                    env: getEnv(),
                    cornerRadius: 16,
                    itemId: pageRawData.itemId,
                    __launch_options__: null,
                    hyId: null,
                    height: 1, // iosrn 新增跳转参数
                    foldRatio: 0.75, // iosrn 新增跳转参数
                    openSlideFullScreen: 1, // iosrn 新增跳转参数 表明是可拖拽的半屏
                    isHalf: 1, // iosrn 新增跳转参数
                    sellerUserId,
                    merchantId,
                    scene,
                    extParams: JSON.stringify(extParamsCustom || extParamsObj),
                },
                { sort: false, skipNull: true },
            );
            _url = queryString.stringifyUrl({
                url: `${getKrnDomain()}/krn-web/detail`,
                query: {
                    hyId: 'kibt-fe-shop-c-krn',
                    cornerRadius: 16,
                    redirectUrl: krnUrl,
                },
            });
        }
        logClickGoToDetail();
        openUrl({ url: _url, halfWebview: true });
    }

    const onBuyCountChange = async (c: number, _: 'minus' | 'plus' | 'edit') => {
        if (c < minNum) {
            Toast.show({ text: $k18n({ id: 'k_251870', text: '最少购买{x}件', params: { x: minNum } }) });
        }
        setPurchaseQuantity(Math.max(c, minNum));
    };
    const getMaxBuyCount = () => {
        if (!(currentSku && currentSku?.skuId)) {
            return Infinity;
        }
        if (currentSku && currentSku?.activityLimitCount > 0 && currentSku?.activityLimitCount < immediateAvailableQuantity) {
            return currentSku?.activityLimitCount;
        }
        if (currentSku && currentSku?.availableQuantity) {
            return currentSku?.availableQuantity as number;
        }
        if (immediateAvailableQuantity) {
            return immediateAvailableQuantity;
        }
        return Infinity;
    };
    const maxBuyCount = Math.max(getMaxBuyCount(), minNum);

    const onBuyCountOverLimit = (t) => {
        if (t === 'minus') {
            Toast.show({ text: $k18n({ id: 'k_251870', text: '最少购买{x}件', params: { x: minNum } }) });
        } else {
            if (purchaseQuantity >= maxBuyCount) {
                Toast.show({ text: $k18n({ id: 'k_352441', text: '最多购买{num}件', params: { num: maxBuyCount } }) });
            }
        }
    };

    const getSpecImageUrls = () => {
        const productMainImage = pageRawData && pageRawData.imageUrls && pageRawData.imageUrls[0];
        // 图片优化的主图
        const productMainImageOpt = pageRawData && pageRawData.templateImageUrls && pageRawData.templateImageUrls[0];
        const imageArr = [productMainImageOpt || productMainImage];

        if (pageRawData?.specs?.[0]?.itemSpecValues?.[0]?.imageUrl && pageRawData?.specs?.[0]?.itemSpecValues?.[0]?.imageUrl !== '')
            pageRawData.specs[0].itemSpecValues?.forEach((item) => {
                imageArr.push(item.templateImageUrl || item.imageUrl);
            });

        return [...imageArr];
    };

    const specImageUrls = getSpecImageUrls();

    const [isFlashProduct, isAfterUsingCoupon] = useLatest([
        pageRawData.salePriceInclude?.includes('FLASH_SALE'),
        pageRawData.salePriceInclude?.includes('COUPON'),
    ]).current;

    const { priceAfterCoupon } = usePriceAfterCoupon();

    const showInstallMentFirst = useLatest(
        !isToBDetail /* 达人商详页不优先展示分期信息 */ &&
            detailABValueMap.ifInstallmentAmountFirst &&
            thresholdValue <= pageRawData.itemPrice &&
            pageRawData.skus?.length === pageRawData.skuInstallments?.length,
    ).current;

    const geSelectItemLabels = () => {
        let specsStr = '';
        pageRawData.specs.forEach((_specs) => {
            const spec = selectedItems?.find((selectedItem) => String(selectedItem?.specId) === String(_specs?.specId));
            if (spec) specsStr = `${specsStr}${spec.specValueName}, `;
        });
        specsStr = specsStr.substring(0, specsStr.length - 2);
        return `${specsStr}`;
    };
    const selectedItemsLabels = geSelectItemLabels();

    const immediateDisplayPrice = useMemo(() => {
        const res = [] as any;
        skuInfo.forEach((skuItem) => {
            // 备注： 若收到一个空数组，此方法在任何情况下都会返回 true。
            // 所以在没有选择 规格 的情况下 就是把当前商品的所有sku都push到res中。
            const flag = selectedItems.every((item) => {
                return skuItem[item.specId] === item.specValueId;
            });
            if (flag) {
                /* 达人商详页不展示分期信息 */
                const installment = isToBDetail
                    ? {}
                    : pageRawData.skuInstallments?.find((_installment) => _installment.skuId === skuItem.skuId) || {};
                res.push({ ...skuItem, installment });
            }
        });
        if (!res.length) {
            return {};
        }
        // 券后价和秒杀价的取值逻辑是一样的，只是取用的字段不一样
        // 商品命中展示券后价实验 || 商详页展示displaySalePrice(showAfterCouponPrice = true)
        // 商品参加活动时，展示秒杀价
        if (
            (isAfterUsingCoupon && priceAfterCoupon.showPriceUsedCouponStyle) ||
            showAfterCouponPrice ||
            (pageRawData?.activityInfo?.activityId &&
                showFlashActivity(pageRawData?.activityInfo?.status) &&
                pageRawData?.activityInfo?.saleRate <= 100)
        ) {
            const typePrice = {
                afterCoupon: {
                    itemPrice: 'salePrice',
                    displayPrice: 'displaySalePrice',
                },
                flashSale: {
                    itemPrice: 'activityPrice',
                    displayPrice: 'displayActivityPrice',
                },
            };
            const targetPrice = !showAfterCouponPrice ? typePrice.flashSale : typePrice.afterCoupon;
            // 对所有的sku 进行价格排序
            const sortedRes = res.sort((a, b) => a[targetPrice.itemPrice] - b[targetPrice.itemPrice]);
            // 如果价格不一样 展示 100起 这种
            if (sortedRes[0] && sortedRes[0][targetPrice.itemPrice] === sortedRes[sortedRes.length - 1][targetPrice.itemPrice]) {
                return {
                    price: sortedRes[0][targetPrice.itemPrice],
                    displayPrice: sortedRes[0][targetPrice.displayPrice],
                    node: <Text style={{ fontSize: 24, fontWeight: '700', color: '#FF4906' }}>{sortedRes[0][targetPrice.displayPrice]}</Text>,
                    ifAllSame: true,
                    installmentInfo: sortedRes[0].installment,
                };
            }
            return {
                price: sortedRes[0]?.[targetPrice.itemPrice],
                displayPrice: sortedRes[0]?.[targetPrice.displayPrice],
                node: <Text style={{ fontSize: 24, fontWeight: '700', color: '#FF4906' }}>{sortedRes[0]?.[targetPrice.displayPrice]}</Text>,
                ifAllSame: false,
                installmentInfo: sortedRes[0]?.installment,
            };
        }
        // 对所有的sku 进行价格排序
        const sortedRes = res.sort((a, b) => a.skuPrice - b.skuPrice);
        // 如果价格不一样 展示 100起 这种
        if (sortedRes[0] && sortedRes[0].displaySkuPrice === sortedRes[sortedRes.length - 1].displaySkuPrice) {
            return {
                price: sortedRes[0].skuPrice,
                displayPrice: sortedRes[0].displaySkuPrice,
                node: <KText rtlClassName={'product-price-display-text'}>{sortedRes[0].displaySkuPrice}</KText>,
                ifAllSame: true,
                installmentInfo: sortedRes[0].installment,
            };
        }
        return {
            price: sortedRes[0].skuPrice,
            displayPrice: sortedRes[0].displaySkuPrice,
            node: <KText rtlClassName={'product-price-display-text'}>{sortedRes[0].displaySkuPrice}</KText>,
            ifAllSame: false,
            installmentInfo: sortedRes[0].installment,
        };
    }, [skuInfo, isAfterUsingCoupon, priceAfterCoupon.showPriceUsedCouponStyle, showAfterCouponPrice, pageRawData, selectedItems]);

    const renderInstallment = () => {
        return (
            <KView>
                {isAfterUsingCoupon && priceAfterCoupon.showPriceUsedCouponStyle && showAfterCouponPrice ? (
                    <KText rtlClassName={'installment-line-top'}>{$k18n({ text: '券后' })}</KText>
                ) : !immediateDisplayPrice.ifAllSame ? (
                    <KText rtlClassName={'installment-line-top'}>{$k18n({ text: '起' })}</KText>
                ) : null}
                {immediateDisplayPrice.installmentInfo ? (
                    <KView rtlClassName={'installment-line'}>
                        <KText rtlClassName={'installment-price__display-text'}>
                            {immediateDisplayPrice.installmentInfo.displayPaymentPerInstallment}
                        </KText>
                        <KText rtlClassName={'installment-month-account'}>
                            {$k18n({ text: '/期', id: 'k_405771' })}&nbsp;x&nbsp;{immediateDisplayPrice.installmentInfo.installment}
                        </KText>
                    </KView>
                ) : null}
            </KView>
        );
    };

    const renderHeaderPrice = () => {
        return showInstallMentFirst ? (
            <KView>
                {renderInstallment()}
                <KView rtlClassName={'product-header-price'}>
                    <KText rtlClassName={'product-header-price-total'}>{$k18n({ text: '总价', id: 'k_245610' })}</KText>
                    <KText rtlClassName={'product-header-price'}>
                        {!immediateDisplayPrice.ifAllSame
                            ? $k18n({ id: 'k_269960', params: { price: immediateDisplayPrice.displayPrice } })
                            : immediateDisplayPrice.displayPrice}
                    </KText>
                    <KText
                        rtlClassName={'product-header-price-break-text'}
                        style={{
                            textDecorationColor: '#999',
                        }}
                    >
                        {currentSku.skuMarketPrice ? currentSku.displaySkuMarketPrice : ''}
                    </KText>
                </KView>
            </KView>
        ) : (
            <View>
                {isAfterUsingCoupon && priceAfterCoupon.showPriceUsedCouponStyle && showAfterCouponPrice ? (
                    <KText rtlClassName={'installment-line-top'}>{$k18n({ text: '券后' })}</KText>
                ) : !immediateDisplayPrice.ifAllSame ? (
                    <KText rtlClassName={'installment-line-top'}>{$k18n({ text: '起' })}</KText>
                ) : null}
                <KView rtlClassName={'product-price'}>
                    <KView rtlClassName={['product-price-display', currentSku.skuMarketPrice ? 'product-price-display-limit-width' : '']}>
                        {immediateDisplayPrice.node}
                    </KView>
                    <KView rtlClassName={'product-price-break'}>
                        <KText rtlClassName={'product-price-break-text'} style={{ textDecorationColor: '#999' }}>
                            {currentSku.skuMarketPrice ? currentSku.displaySkuMarketPrice : ''}
                        </KText>
                    </KView>
                </KView>
            </View>
        );
    };

    return (
        <KView rtlClassName={'spec_header'}>
            <KView>
                <TouchableWithoutFeedback onPress={() => clickImgPre()}>
                    <KView rtlClassName={'spec_header_img_wrapper'}>
                        {/* <KImage
                            rtlClassName={'spec_header_img'}
                            source={{ uri: specHeaderImageSource }}
                            cropOption={{ ratio: 0.5 }}
                        /> */}
                        <CropImage
                            className={Styles.spec_header_img}
                            src={isNotAllImg ? specImageUrls[0] : specImageUrls[specImageIndex === -1 ? 0 : specImageIndex + 1]}
                            cropOption={{ ratio: 0.5, width: 120, height: 120 }}
                            width={120}
                            height={120}
                            logParams={{
                                itemId: pageRawData?.itemId,
                                position: 'spec-line-pop',
                            }}
                        />
                        <KImage rtlClassName={'spec_header_img_enlarge'} source={EnlargePng} />
                    </KView>
                </TouchableWithoutFeedback>
            </KView>
            <KView rtlClassName={'infos'}>
                {renderHeaderPrice()}
                <KView>
                    <KView rtlClassName={'infos__btm'}>
                        {pageRawData?.extraInfo?.selfOperated && type === 'cart' ? null : (
                            <KView rtlClassName={'inventory'}>
                                <KText rtlClassName={'inventory-text'}>
                                    {$k18n({ id: 'k_269958', text: '库存' })}{' '}
                                    {newFormatNumber(immediateAvailableQuantity < minNum ? 0 : immediateAvailableQuantity, currentCountry)}
                                </KText>
                            </KView>
                        )}
                    </KView>
                    <KView rtlClassName={'infos_spec'}>
                        {selectedItemsLabels ? (
                            <KText numberOfLines={2} ellipsizeMode={'tail'} rtlClassName={'skuspecdesc-text'}>
                                {selectedItemsLabels}
                            </KText>
                        ) : (
                            <KText rtlClassName={'tip-text'}>{$k18n('请选择规格')}</KText>
                        )}
                    </KView>
                    {showDetailUrl ? (
                        <TouchableWithoutFeedback onPress={() => goDetail(showDetailUrl)}>
                            <KView style={{ display: 'flex', flexDirection: 'row' }}>
                                <KText numberOfLines={1} ellipsizeMode={'tail'} rtlClassName={'tip-text'}>
                                    {$k18n({ id: 'fa8b956e5096d04a349fb115253d75f9', text: '商品详情' })}
                                </KText>
                                <KImage style={{ width: 24, height: 24, opacity: 0.4, transform: [{ rotate: '-90deg' }] }} source={IconDown} />
                            </KView>
                        </TouchableWithoutFeedback>
                    ) : null}
                </KView>
                {showUpStepper && !pageRawData?.extraInfo?.selfOperated ? (
                    <Stepper
                        fallbackWhenEmpty={true}
                        disableText
                        min={minNum}
                        max={Math.max(maxBuyCount, minNum)}
                        value={purchaseQuantity}
                        onPlus={(val) => onBuyCountChange(val, 'plus')}
                        onMinus={(val) => onBuyCountChange(val, 'minus')}
                        onChange={(val) => onBuyCountChange(val, 'edit')}
                        onOverLimit={onBuyCountOverLimit}
                    />
                ) : null}
            </KView>
        </KView>
    );
}
